import { type FilterOption } from '@/types';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface CategoryTreeNode extends FilterOption {
    children: CategoryTreeNode[];
}

interface CategoryTreeProps {
    categories: FilterOption[];
    selectedCategories: string[];
    onSelectionChange: (nextSelectedCategories: string[]) => void;
}

export const CategoryTree: React.FC<CategoryTreeProps> = ({ categories, selectedCategories, onSelectionChange }) => {
    const categoryTree = useMemo(() => {
        const categoryMap = new Map<number, CategoryTreeNode>();
        const rootCategories: CategoryTreeNode[] = [];

        categories.forEach((category) => {
            categoryMap.set(category.id, { ...category, children: [] });
        });

        categories.forEach((category) => {
            const node = categoryMap.get(category.id);
            if (!node) return;

            if (category.parent_id === null || category.parent_id === undefined) {
                rootCategories.push(node);
            } else {
                const parent = categoryMap.get(category.parent_id);
                if (parent) {
                    parent.children.push(node);
                } else {
                    rootCategories.push(node);
                }
            }
        });

        return rootCategories;
    }, [categories]);

    const categoryById = useMemo(() => {
        return new Map(categories.map((category) => [category.id, category]));
    }, [categories]);

    const hasAnySelectedDescendant = (node: CategoryTreeNode): boolean =>
        node.children.some((c) => selectedCategories.includes(c.id.toString()) || hasAnySelectedDescendant(c));

    const collectDescendantIds = (node: CategoryTreeNode): string[] => {
        const ids: string[] = [];
        for (const child of node.children) {
            ids.push(child.id.toString());
            ids.push(...collectDescendantIds(child));
        }
        return ids;
    };

    const collectAncestorIds = (categoryId: number): string[] => {
        const ids: string[] = [];
        let current = categoryById.get(categoryId);

        while (current && current.parent_id != null) {
            ids.push(current.parent_id.toString());
            current = categoryById.get(current.parent_id);
        }

        return ids;
    };

    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

    useEffect(() => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            const expandAncestors = (nodes: CategoryTreeNode[]) => {
                for (const node of nodes) {
                    if (hasAnySelectedDescendant(node)) next.add(node.id);
                    expandAncestors(node.children);
                }
            };
            expandAncestors(categoryTree);
            return next;
        });
    }, [categoryTree, selectedCategories]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleNode = (categoryId: number) => {
        setExpandedNodes((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const renderNode = (node: CategoryTreeNode, level: number = 0): React.ReactNode => {
        const isExpanded = expandedNodes.has(node.id);
        const hasChildren = node.children.length > 0;

        const isExplicitlyChecked = selectedCategories.includes(node.id.toString());
        const isImplicitlyChecked = !isExplicitlyChecked && hasAnySelectedDescendant(node);
        const isChecked = isExplicitlyChecked || isImplicitlyChecked;

        const handleChange = (checked: boolean) => {
            const nextSelected = new Set(selectedCategories);
            const descendantIds = collectDescendantIds(node);

            if (checked) {
                descendantIds.forEach((id) => nextSelected.delete(id));
                collectAncestorIds(node.id).forEach((id) => nextSelected.delete(id));
                nextSelected.add(node.id.toString());
            } else if (isImplicitlyChecked) {
                descendantIds.forEach((id) => nextSelected.delete(id));
            } else {
                nextSelected.delete(node.id.toString());
            }

            onSelectionChange(Array.from(nextSelected));
        };

        return (
            <div key={node.id} className="space-y-1">
                <div className={`flex items-center space-x-2 ${level > 0 ? 'ml-4' : ''}`}>
                    {hasChildren && (
                        <button onClick={() => toggleNode(node.id)} className="rounded p-0.5 transition-colors hover:bg-gray-100">
                            {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                            ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                            )}
                        </button>
                    )}
                    {!hasChildren && <div className="w-4" />}

                    <label className="flex flex-1 cursor-pointer items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handleChange(e.target.checked)}
                            className="rounded border-gray-300"
                        />
                        <span className={`text-sm ${isChecked ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                            {node.name}
                            {node.count !== undefined && <span className="ml-1 font-normal text-gray-500">({node.count})</span>}
                        </span>
                    </label>
                </div>

                {hasChildren && isExpanded && <div className="space-y-1">{node.children.map((child) => renderNode(child, level + 1))}</div>}
            </div>
        );
    };

    if (categoryTree.length === 0) {
        return <p className="text-sm text-gray-500 italic">Nenhuma categoria disponível</p>;
    }

    return <div className="space-y-1">{categoryTree.map((node) => renderNode(node))}</div>;
};